import React from 'react';
import { PopoverContent } from '@fiverr-private/popover';
import { useElementSize } from '@fiverr-private/hooks';
import { Box } from '@fiverr-private/theme';
import { renderChildren, findChildByType, excludeChildrenByType } from '@fiverr-private/ui_utils';
import { Container } from '@fiverr-private/layout_components';
import { DropdownMenuContext } from '../DropdownMenu/useMenuContext';
import useDropdownContext from '../Dropdown/useDropdownContext';
import type { DropdownPopoverContentProps } from '../Dropdown/types';
import type { DropdownContentProps } from './types';
import useContent from './useContent';
import { getStylingProps } from './styles';

const DropdownContent = ({
  children,
  appendToBody,
  disableExitAnimation = false,
  fullWidth,
  closeOnClickOutside,
  zIndex,
  goExperimental = false,
  templateColumns,
  width,
  variant = 'default',
  maxHeight,
  dataTrackTag = 'dropdown',
  dataTrackValue,
}: DropdownContentProps & DropdownPopoverContentProps) => {
  const { searchWhenClosed, anchorRef, isDark: dropdownIsDark, isMobileView } = useDropdownContext();
  const content = useContent();
  const { width: anchorWidth } = useElementSize(anchorRef.current);
  const isDark = goExperimental || dropdownIsDark;
  const isSectionsVariant = variant === 'sections';
  const footer = findChildByType(children, 'DropdownFooter');
  const restChildren = excludeChildrenByType(children, 'DropdownFooter');
  const childrenArray = React.Children.toArray(restChildren);
  const contentDisplay = isSectionsVariant && !isMobileView ? 'grid' : undefined;

  return (
    <PopoverContent
      fullWidth={isSectionsVariant || fullWidth}
      disablePadding
      renderWhenClosed={searchWhenClosed}
      disableBoxShadow
      appendToBody={appendToBody}
      animationType="slideFade"
      onAnimationStart={content.onAnimationStart}
      disableAutoFocus={true}
      minWidth={anchorWidth}
      disableExitAnimation={disableExitAnimation}
      closeOnClickOutside={closeOnClickOutside}
      zIndex={zIndex}
      backgroundColor={isDark ? 'transparent' : undefined}
      dataTrackTag={dataTrackTag}
      dataTrackValue={dataTrackValue}
    >
      <DropdownMenuContext.Provider value={{ ...content, fullWidth, isDark, variant }}>
        <Container {...getStylingProps({ isDark })}>
          <Container overflowX="auto">
            <Box
              gap={isSectionsVariant ? '4' : undefined}
              overflowY={isSectionsVariant ? 'auto' : undefined}
              ref={content.contentRef}
              boxSizing="borderBox"
              width={isMobileView && isSectionsVariant ? 'fit-content' : width}
              display={contentDisplay}
              maxHeight={maxHeight}
              templateColumns={templateColumns}
            >
              {isSectionsVariant
                ? React.Children.map(childrenArray, (child, index) =>
                    renderChildren(child, { withDivider: index < Number(restChildren.length) - 1 })
                  )
                : restChildren}
            </Box>
          </Container>
          {footer}
        </Container>
      </DropdownMenuContext.Provider>
    </PopoverContent>
  );
};

DropdownContent.id = 'DropdownContent';
DropdownContent.displayName = 'DropdownContent';

export default DropdownContent;
